import React, { useState } from 'react';
import { 
  TruckIcon,
  PlusIcon,
  TrashIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { Mission, AircraftContractor } from '../../types';
import { format } from 'date-fns';
import { formatPhoneNumber } from '../../utils/phoneUtils';

function getEquipmentList(selectedEquipment: string[] | undefined): string[] {
  if (!selectedEquipment) return [];
  if (Array.isArray(selectedEquipment)) return selectedEquipment;
  // Handle JSON string from API — only arrays are valid here. The backend
  // stores selectedEquipment as a stringified object in some cases, which
  // parses to a non-array; guard against it so .includes()/.map() don't throw.
  if (typeof selectedEquipment === 'string' && (selectedEquipment as string).length > 0) {
    try {
      const parsed = JSON.parse(selectedEquipment as string);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  return [];
}

interface ResourcesTabProps {
  mission: Mission;
  onUpdate: (updates: Partial<Mission>) => void;
  onAddAircraft: (contractorId: string, notes?: string) => void;
  onRemoveAircraft: (aircraftId: string) => void;
  contractors: AircraftContractor[];
  canEdit: boolean;
}

const ResourcesTab: React.FC<ResourcesTabProps> = ({ 
  mission, 
  onUpdate,
  onAddAircraft,
  onRemoveAircraft,
  contractors,
  canEdit 
}) => {
  const [showAddAircraft, setShowAddAircraft] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [aircraftNotes, setAircraftNotes] = useState('');
  const [localValues, setLocalValues] = useState<Partial<Mission>>({});

  const handleAddAircraft = () => {
    if (selectedContractor) {
      onAddAircraft(selectedContractor, aircraftNotes);
      setSelectedContractor('');
      setAircraftNotes('');
      setShowAddAircraft(false);
    }
  };

  const handleFieldChange = (field: keyof Mission, value: any) => {
    setLocalValues({ ...localValues, [field]: value });
  };

  const handleFieldBlur = (field: keyof Mission) => {
    if (localValues[field] !== undefined && localValues[field] !== mission[field]) {
      onUpdate({ [field]: localValues[field] });
      setLocalValues({ ...localValues, [field]: undefined });
    }
  };

  const handleGroundResourceChange = (resourceName: string, checked: boolean) => {
    const currentResources = getEquipmentList(mission.groundResources);
    let updatedResources: string[];

    if (checked) {
      updatedResources = [...currentResources, resourceName];
    } else {
      updatedResources = currentResources.filter((item: string) => item !== resourceName);
    }

    onUpdate({ groundResources: updatedResources });
  };

  const handleEquipmentChange = (equipmentName: string, checked: boolean) => {
    const currentEquipment = getEquipmentList(mission.selectedEquipment);
    let updatedEquipment: string[];

    if (checked) {
      updatedEquipment = [...currentEquipment, equipmentName];
    } else {
      updatedEquipment = currentEquipment.filter((item: string) => item !== equipmentName);
    }
    
    onUpdate({ selectedEquipment: updatedEquipment });
  };

  return (
    <div className="space-y-6">
      {/* Aircraft/Contractors */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center justify-between">
            <span className="flex items-center">
              <TruckIcon className="h-5 w-5 mr-2" />
              Aircraft & Contractors
            </span>
            {canEdit && (
              <button
                onClick={() => setShowAddAircraft(true)}
                className="btn btn-primary btn-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Aircraft
              </button>
            )}
          </h3>
        </div>

        {/* Add Aircraft Form */}
        {showAddAircraft && (
          <div className="p-4 bg-blue-50 rounded-lg mb-4">
            <h4 className="font-medium text-blue-900 mb-3">Assign Aircraft/Contractor</h4>
            
            <div className="space-y-3">
              <div>
                <label className="form-label">Select Contractor</label>
                <select
                  value={selectedContractor}
                  onChange={(e) => setSelectedContractor(e.target.value)}
                  className="form-input"
                >
                  <option value="">Choose contractor...</option>
                  {contractors.filter(c => c.active).map(contractor => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.companyName}
                      {contractor.aircraftType && ` - ${contractor.aircraftType}`}
                      {contractor.dispatchPhone && ` - ${formatPhoneNumber(contractor.dispatchPhone)}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  value={aircraftNotes}
                  onChange={(e) => setAircraftNotes(e.target.value)}
                  className="form-input"
                  rows={2}
                  placeholder="Tail number, crew names, special equipment..."
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAddAircraft(false);
                    setSelectedContractor('');
                    setAircraftNotes('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAircraft}
                  className="btn btn-primary"
                  disabled={!selectedContractor}
                >
                  Add Aircraft
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Aircraft List */}
        {mission.aircraft && mission.aircraft.length > 0 ? (
          <div className="space-y-3">
            {mission.aircraft.map((aircraft) => (
              <div key={aircraft.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                      <h4 className="font-semibold">
                        {aircraft.contractor?.companyName || 'Unknown Contractor'}
                      </h4>
                      {aircraft.tailNumber && (
                        <span className="text-sm text-gray-600">
                          ({aircraft.tailNumber})
                        </span>
                      )}
                    </div>
                    
                    {aircraft.aircraftType && (
                      <p className="text-sm text-gray-600 mb-1">
                        Aircraft: {aircraft.aircraftType}
                      </p>
                    )}
                    
                    {/* Dispatch Phone - Primary Emergency Contact */}
                    {aircraft.contractor?.dispatchPhone && (
                      <div className="flex items-center gap-1 text-sm mb-1 p-2 bg-red-50 rounded border-l-4 border-red-500">
                        <PhoneIcon className="h-4 w-4 text-red-600" />
                        <span className="font-semibold text-red-800">DISPATCH: </span>
                        <span className="font-mono text-red-900">{formatPhoneNumber(aircraft.contractor.dispatchPhone)}</span>
                      </div>
                    )}
                    
                    {/* Contact Phone - Secondary Contact */}
                    {aircraft.contractor?.contactPhone && (
                      <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                        <PhoneIcon className="h-4 w-4" />
                        <span>Contact: </span>
                        <span className="font-mono">{formatPhoneNumber(aircraft.contractor.contactPhone)}</span>
                        {aircraft.contractor.contactName && ` (${aircraft.contractor.contactName})`}
                      </div>
                    )}
                    
                    {aircraft.notes && (
                      <p className="text-sm text-gray-700 mt-2">{aircraft.notes}</p>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Assigned: {format(new Date(aircraft.assignedAt), 'HH:mm dd/MM/yyyy')}
                    </p>
                  </div>
                  
                  {canEdit && (
                    <button
                      onClick={() => onRemoveAircraft(aircraft.id)}
                      className="text-red-600 hover:text-red-800 ml-4"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No aircraft assigned</h3>
            <p className="mt-1 text-sm text-gray-500">
              Assign aircraft and contractors as they become available.
            </p>
          </div>
        )}
      </div>

      {/* Ground Resources */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title">Ground Resources</h3>
        </div>
        
        <div className="space-y-3">
          {[
            'BC Ambulance Service on scene',
            'Fire Department support',
            'RCMP/Police on scene',
            'Search & Rescue teams',
            'Ground transport arranged'
          ].map((resource) => (
            <label key={resource} className="flex items-center">
              <input
                type="checkbox"
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={getEquipmentList(mission.groundResources).includes(resource)}
                onChange={(e) => handleGroundResourceChange(resource, e.target.checked)}
                disabled={!canEdit}
              />
              <span className="text-sm font-medium text-gray-700">{resource}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Special Equipment */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
            Special Equipment
          </h3>
        </div>
        
        <div className="space-y-3">
          {[
            'CDFL/Hoist',
            'Auto Extrication Gear',
            'Swift Water',
            'ACGM Rescue',
            'Night Vision Goggles',
            'Winch/Long Line',
            'External Load Equipment',
            'Mountain Rescue Gear'
          ].map((equipment) => (
            <label key={equipment} className="flex items-center">
              <input 
                type="checkbox" 
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={getEquipmentList(mission.selectedEquipment).includes(equipment)}
                onChange={(e) => handleEquipmentChange(equipment, e.target.checked)}
                disabled={!canEdit}
              />
              <span className="text-sm font-medium text-gray-700">{equipment}</span>
            </label>
          ))}
        </div>
        
        {getEquipmentList(mission.selectedEquipment).length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Selected Equipment:</h4>
            <div className="flex flex-wrap gap-2">
              {getEquipmentList(mission.selectedEquipment).map((equipment: string) => (
                <span key={equipment} className="px-2 py-1 bg-blue-200 text-blue-800 text-xs rounded-full">
                  {equipment}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Resource Notes */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Resource Notes
          </h3>
        </div>
        
        <textarea
          value={localValues.resourceNotes !== undefined ? localValues.resourceNotes : (mission.resourceNotes || '')}
          onChange={(e) => handleFieldChange('resourceNotes', e.target.value)}
          onBlur={() => handleFieldBlur('resourceNotes')}
          className="form-input"
          rows={4}
          placeholder="Additional resource information, special equipment needs, etc..."
          disabled={!canEdit}
        />
      </div>

      {/* Resource Summary */}
      <div className="card bg-blue-50">
        <div className="card-header border-b border-blue-200">
          <h3 className="card-title text-blue-900">Resource Summary</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-blue-700">Total Aircraft</p>
            <p className="text-2xl font-semibold text-blue-900">
              {mission.aircraft?.length || 0}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-blue-700">Active Contractors</p>
            <p className="text-2xl font-semibold text-blue-900">
              {new Set(mission.aircraft?.map(a => a.contractorId)).size || 0}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-blue-700">Response Time</p>
            <p className="text-2xl font-semibold text-blue-900">
              {mission.timeAircraftLaunched && mission.timeReceived
                ? `${Math.round(
                    (new Date(mission.timeAircraftLaunched).getTime() - 
                     new Date(mission.timeReceived).getTime()) / 60000
                  )} min`
                : 'N/A'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Quick Reference for Available Contractors */}
      <div className="card bg-yellow-50">
        <div className="card-header border-b border-yellow-200">
          <h3 className="card-title text-yellow-900">Quick Reference - Available Contractors</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto">
          {contractors.filter(c => c.active && c.dispatchPhone).map((contractor) => (
            <div key={contractor.id} className="p-2 bg-white rounded border border-yellow-200">
              <div className="font-medium text-sm text-gray-900 mb-1">
                {contractor.companyName}
              </div>
              <div className="flex items-center gap-1 text-xs">
                <PhoneIcon className="h-3 w-3 text-red-500" />
                <span className="font-mono text-red-700 font-semibold">
                  {formatPhoneNumber(contractor.dispatchPhone!)}
                </span>
              </div>
              {contractor.aircraftType && (
                <div className="text-xs text-gray-600 mt-1">
                  {contractor.aircraftType}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {canEdit && (
        <div className="text-sm text-gray-500 italic">
          Tip: All fields auto-save when you click away or press Tab
        </div>
      )}
    </div>
  );
};

export default ResourcesTab;